import type {
  CreateZeroEtlPolicyInput,
  UpdateZeroEtlPolicyInput,
  ZeroEtlPolicy,
} from '@repo/api-gw-sdk';
import { ZeroEtlRoutingMode } from '@repo/api-gw-sdk';

import { getResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { Wizard } from '@/components/wizard/Wizard';
import {
  createAllIncludedCondition,
  createAllExcludedCondition,
} from '@/types/advanceFilter';

import {
  EditZeroEtlPolicyFlow,
  type ZeroEtlPolicyEditState,
} from './ZeroEtlPolicyCreationFlow';

interface ZeroEtlPolicySidePanelProps {
  entity?: ZeroEtlPolicy;
  workspaceId: string;
  onSave: (
    input: CreateZeroEtlPolicyInput | UpdateZeroEtlPolicyInput,
    policyId?: string
  ) => Promise<void>;
  onAbort: () => void;
}

export function ZeroEtlPolicySidePanel(props: ZeroEtlPolicySidePanelProps) {
  const entity = props.entity;
  const skipFirstStep = !!entity;
  const creatingNew = !entity;
  const policyId = entity?.id || '';

  const save = async (state: ZeroEtlPolicyEditState) => {
    const condition =
      state.resourcesScopeType === 'allIncluded'
        ? createAllIncludedCondition()
        : state.resourcesScopeType === 'allExcluded'
          ? createAllExcludedCondition()
          : state.condition;

    if (!condition) {
      return false;
    }

    const input: CreateZeroEtlPolicyInput | UpdateZeroEtlPolicyInput = {
      name: state.name,
      enabled: entity?.enabled ?? true,
      resourceSelector: {
        condition,
        include: state.include ?? [],
        exclude: state.exclude ?? [],
      },
      catalogIds: state.catalogIds,
      routingPolicy: {
        routingMode: state.routingMode,
        ...(state.routingMode ===
          ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_MANUAL && {
          namespaceTemplate: state.namespaceTemplate,
          tableTemplate: state.tableTemplate,
        }),
        ...(state.identityColumns.length > 0 && {
          identityColumns: state.identityColumns,
        }),
      },
    };

    await props.onSave(input, policyId || undefined);
    return true;
  };

  const title = policyId
    ? 'Edit Zero-ETL policy'
    : creatingNew
      ? 'Create Zero-ETL policy'
      : 'Duplicate Zero-ETL policy';

  return (
    <Wizard<ZeroEtlPolicyEditState>
      onAbort={props.onAbort}
      hideBackArrow={true}
      onFinish={save}
      initialStep={{ stepIndex: skipFirstStep ? 1 : 0 }}
      flow={[EditZeroEtlPolicyFlow]}
      initialState={{
        id: policyId,
        name: entity?.name ?? '',
        workspaceId: props.workspaceId,
        focusOnName: skipFirstStep && !policyId,
        condition: entity?.resourceSelector?.condition,
        include: entity?.resourceSelector?.include ?? [],
        exclude: entity?.resourceSelector?.exclude ?? [],
        catalogIds: entity?.catalogIds ?? [],
        resourcesScopeType: getResourcesScopeType(
          entity?.resourceSelector?.condition,
          'useConditions'
        ),
        routingMode:
          entity?.routingPolicy?.routingMode ??
          ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED,
        namespaceTemplate: entity?.routingPolicy?.namespaceTemplate ?? '',
        tableTemplate: entity?.routingPolicy?.tableTemplate ?? '',
        identityColumns: entity?.routingPolicy?.identityColumns ?? [],
      }}
      title={title}
      stepperLabels={[
        'Resource selection',
        'Resource list',
        'Catalog & Routing',
      ]}
    />
  );
}
